import Ajv2020, { type ErrorObject } from 'ajv/dist/2020';
import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { Layout, RawPalette, RawPainterView, DataError } from './types';

import layoutSchema from './schema/layout.schema.json';
import paletteSchema from './schema/palette.schema.json';
import painterViewSchema from './schema/painterView.schema.json';

export interface Validators {
  layout(value: unknown): Result<Layout, DataError>;
  palette(value: unknown): Result<RawPalette, DataError>;
  painterView(value: unknown): Result<RawPainterView, DataError>;
}

export function createAjvFactory(): Ajv2020 {
  return new Ajv2020({ strict: true, allErrors: true });
}

function formatAjvErrors(errors: ErrorObject[] | null | undefined, file: string): DataError {
  const errorList = errors ?? [];
  const details = errorList.map((e) => `${e.instancePath || '/'}: ${e.message ?? 'unknown error'}`);
  return {
    file: file as 'layout' | 'palette' | 'painterView',
    kind: 'schema',
    message: `Schema validation failed for ${file}`,
    details,
  };
}

let cachedValidators: Validators | null = null;

export function createValidators(): Validators {
  if (cachedValidators) {
    return cachedValidators;
  }

  const ajv = createAjvFactory();

  const validateLayout = ajv.compile(layoutSchema);
  const validatePalette = ajv.compile(paletteSchema);
  const validatePainterView = ajv.compile(painterViewSchema);

  const validators: Validators = {
    layout(value: unknown): Result<Layout, DataError> {
      if (validateLayout(value)) {
        return ok(value as unknown as Layout);
      }
      return err(formatAjvErrors(validateLayout.errors, 'layout'));
    },

    palette(value: unknown): Result<RawPalette, DataError> {
      if (validatePalette(value)) {
        return ok(value as unknown as RawPalette);
      }
      return err(formatAjvErrors(validatePalette.errors, 'palette'));
    },

    painterView(value: unknown): Result<RawPainterView, DataError> {
      if (validatePainterView(value)) {
        return ok(value as unknown as RawPainterView);
      }
      return err(formatAjvErrors(validatePainterView.errors, 'painterView'));
    },
  };

  cachedValidators = validators;
  return validators;
}

export function resetValidatorsCache(): void {
  cachedValidators = null;
}
